import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import Header from "@/components/Header";
import StatCard from "@/components/StatCard";
import TradeHistorySection from "@/components/TradeHistorySection";
import { money, pnlFmt, pnlColor } from "@/lib/format";
import { Wallet, TrendingUp, PiggyBank, Activity, ArrowDownToLine } from "lucide-react";

const GOLD_IMG =
  "https://images.unsplash.com/photo-1718752773195-c19c1c329156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxnb2xkJTIwYnVsbGlvbiUyMGJhciUyMGx1eHVyeSUyMGZpbmFuY2V8ZW58MHx8fHwxNzg5NjY1NDExfDA&ixlib=rb-4.1.0&q=85";

export default function ClientDashboard() {
  const [ov, setOv] = useState(null);
  const [trades, setTrades] = useState([]);
  const [txs, setTxs] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, t, x] = await Promise.all([
        api.get("/client/overview"),
        api.get("/client/trades"),
        api.get("/client/transactions"),
      ]);
      setOv(o.data);
      setTrades(t.data);
      setTxs(x.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="client-dashboard">
      <Header />
      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-6 sm:px-6">
        <div className="relative overflow-hidden rounded-lg border border-[#2A2E39] fade-up">
          <img src={GOLD_IMG} alt="Gold bullion" className="h-36 w-full object-cover opacity-40" />
          <div className="absolute inset-0 flex flex-col justify-center bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/75 to-transparent px-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0B90B]">Managed Gold Account</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#EAECEF] sm:text-3xl">
              Welcome, {ov?.name ?? "..."}
            </h1>
            <div className="mt-1 font-mono text-xs text-[#848E9C]">Account #{ov?.account_no}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5 fade-up">
          <div data-testid="client-balance-display">
            <StatCard testid="client-balance-card" label="Balance" value={money(ov?.balance ?? 0)} icon={Wallet} />
          </div>
          <div data-testid="client-equity-display">
            <StatCard testid="client-equity-card" label="Equity" value={money(ov?.equity ?? 0)} icon={Activity} accent="#EAECEF" sub={`Floating: ${pnlFmt(ov?.floating_pnl ?? 0)}`} />
          </div>
          <div data-testid="client-pnl-display">
            <StatCard
              testid="client-pnl-card"
              label="Total P&L"
              value={pnlFmt(ov?.total_pnl ?? 0)}
              icon={TrendingUp}
              accent={(ov?.total_pnl ?? 0) >= 0 ? "#0ECB81" : "#F6465D"}
              sub={`${ov?.closed_trades ?? 0} closed trades`}
            />
          </div>
          <StatCard testid="client-net-deposits-card" label="Net Deposits" value={money(ov?.net_deposits ?? 0)} icon={PiggyBank} accent="#EAECEF" />
          <StatCard testid="client-deposits-card" label="Deposits / Withdrawals" value={money(ov?.total_deposits ?? 0)} icon={ArrowDownToLine} sub={`Withdrawn: ${money(ov?.total_withdrawals ?? 0)}`} />
        </div>

        <TradeHistorySection trades={trades} openCount={ov?.open_positions ?? 0} />

        <div className="space-y-3 fade-up">
          <h2 className="font-heading text-lg font-semibold text-[#EAECEF]">Deposits & Withdrawals</h2>
          <div className="overflow-x-auto rounded-lg border border-[#2A2E39] bg-[#131722]">
            <table data-testid="client-deposit-withdraw-ledger-table" className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-[#2A2E39]">
                  {["Date", "Transaction ID", "Type", "Method", "Note", "Amount", "Status", "Balance After"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-sm text-[#5E6673]">
                      No transactions yet
                    </td>
                  </tr>
                )}
                {txs.map((t) => (
                  <tr key={t.id} data-testid={`tx-row-${t.id.slice(0, 8)}`} className="border-b border-[#1E222D] transition-colors hover:bg-[#1E222D]/60">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[#848E9C]">
                      {dayjs(t.created_at).format("YYYY.MM.DD HH:mm")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[#5E6673]">TX-{t.id.slice(0, 8).toUpperCase()}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ${
                          t.type === "deposit"
                            ? "bg-[rgba(14,203,129,0.12)] text-[#0ECB81]"
                            : "bg-[rgba(246,70,93,0.12)] text-[#F6465D]"
                        }`}
                      >
                        {t.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-[#EAECEF]">{t.method}</td>
                    <td className="max-w-[180px] truncate px-3 py-2 text-xs text-[#5E6673]">{t.note || "—"}</td>
                    <td className={`whitespace-nowrap px-3 py-2 text-right font-mono font-semibold ${t.type === "deposit" ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                      {t.type === "deposit" ? "+" : "-"}{money(t.amount)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="rounded border border-[#F0B90B]/40 bg-[#F0B90B]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#F0B90B]">
                        {t.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[#EAECEF]">{money(t.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
